import React, { useState, useRef, useEffect } from "react";
import "./musicPlayer.css";
import trackAudio from "../../music/FLIGHT_128bpm_@KIK$$.mp3";
import coverImage from "../../music/cover/cover.jpg";

const MusicPlayer = () => {
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState(null);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((err) => {
            console.error("Playback error:", err);
            setIsPlaying(false);
          });
      }
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current || isDragging) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const calculateTimeFromEvent = (e) => {
    if (!progressBarRef.current || !duration) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const seekPercentage = Math.max(0, Math.min(1, clickX / width));
    return seekPercentage * duration;
  };

  const handlePointerDown = (e) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    if (!audioRef.current || !duration) return;

    if (e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {}
    }

    setIsDragging(true);
    const newTime = calculateTimeFromEvent(e);
    setDragTime(newTime);
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !duration) return;
    const newTime = calculateTimeFromEvent(e);
    setDragTime(newTime);
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;

    if (e.currentTarget.releasePointerCapture) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }

    const finalTime = calculateTimeFromEvent(e);
    if (audioRef.current) {
      audioRef.current.currentTime = finalTime;
    }
    setCurrentTime(finalTime);
    setIsDragging(false);
    setDragTime(null);
  };

  const handlePointerCancel = (e) => {
    if (!isDragging) return;

    if (e.currentTarget.releasePointerCapture) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }

    setIsDragging(false);
    setDragTime(null);
  };

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || !isFinite(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const displayTime = isDragging && dragTime !== null ? dragTime : currentTime;
  const progressPercent = duration > 0 ? (displayTime / duration) * 100 : 0;

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio && typeof audio.pause === "function") {
        try {
          audio.pause();
        } catch (_) {
          // Ignore in environments without audio support (e.g. test environments)
        }
      }
    };
  }, []);

  return (
    <div className="musicPlayer">
      <audio
        ref={audioRef}
        src={trackAudio}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      {/* Cover Art */}
      <div
        className={`musicPlayer__coverWrapper ${isPlaying ? "isPlaying" : ""}`}
      >
        <img
          src={coverImage}
          alt="FLIGHT - Produced By Me"
          className="musicPlayer__cover"
        />
        {isPlaying && (
          <div className="musicPlayer__coverOverlay">
            <span className="coverPulse" />
          </div>
        )}
      </div>

      {/* Center: Track details & progress bar */}
      <div className="musicPlayer__details">
        <div className="musicPlayer__header">
          <div className="musicPlayer__titles">
            <span className="musicPlayer__trackName">FLIGHT</span>
            <span className="musicPlayer__artist">Produced By Me</span>
          </div>

          {/* Equalizer Waveform animation */}
          <div
            className={`musicPlayer__equalizer ${isPlaying ? "active" : ""}`}
            title={isPlaying ? "Playing" : "Paused"}
          >
            <span className="eqBar eqBar--1" />
            <span className="eqBar eqBar--2" />
            <span className="eqBar eqBar--3" />
            <span className="eqBar eqBar--4" />
          </div>
        </div>

        {/* Progress Bar & Timers */}
        <div className="musicPlayer__progressRow">
          <span className="musicPlayer__time">{formatTime(displayTime)}</span>
          <div
            className={`musicPlayer__progressBar ${isDragging ? "isDragging" : ""}`}
            ref={progressBarRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={displayTime}
            aria-label="Seek progress"
          >
            <div
              className="musicPlayer__progressFill"
              style={{ width: `${progressPercent}%` }}
            >
              <span className="musicPlayer__thumb" />
            </div>
          </div>
          <span className="musicPlayer__time">{formatTime(duration)}</span>
        </div>
      </div>

      {/* Play / Pause Button */}
      <button
        type="button"
        className="musicPlayer__playBtn"
        onClick={togglePlay}
        aria-label={isPlaying ? "Pause FLIGHT" : "Play FLIGHT"}
      >
        {isPlaying ? (
          /* Pause Icon */
          <svg
            className="musicPlayer__icon"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
          </svg>
        ) : (
          /* Play Icon */
          <svg
            className="musicPlayer__icon musicPlayer__icon--play"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default MusicPlayer;
